import {
  auditEventSchema,
  customerSchema,
  itemRegisterSchema,
  itemSchema,
  itemUsageSchema,
  partSchema,
  siteNoteSchema,
  sitePenCountsSchema,
  siteSchema,
  subscriptionSchema,
  unitSchema,
} from '../domain/validation';
import type {
  AuditEvent,
  AuditFilters,
  CreateItemInput,
  CreateSubscriptionInput,
  Customer,
  Item,
  ItemFilters,
  ItemPatch,
  ItemUsage,
  Page,
  Part,
  Site,
  SiteContactPatch,
  Subscription,
  SubscriptionFeature,
  SubscriptionPatch,
  Unit,
} from '../domain/inventory';
import type { ItemRegister } from '../domain/itemRegister';
import type { SiteNote, SiteNoteInput, SiteNotePage } from '../domain/siteNote';
import type { SitePenCounts } from '../domain/sitePenCounts';
import {
  ApiError,
  ConflictError,
  DuplicateError,
  ForbiddenError,
  NotFoundError,
  ValidationError,
} from './errors';
import type { InventoryApi } from './inventory-api';
import { withoutUndefined } from './patch';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

// Canonical gRPC status codes the backend returns as grpc-gateway JSON.
// ABORTED is what UpdateSite reports for a stale version.
const GRPC_CODES: Record<number, string> = {
  5: 'NOT_FOUND',
  7: 'FORBIDDEN', // PERMISSION_DENIED
  10: 'CONFLICT', // ABORTED
};

interface RawSite extends Record<string, unknown> {
  locationInfo?: { latitude?: unknown; longitude?: unknown };
}

// A 401 from the relay means the session is gone — missing, expired, or an
// unrefreshable id_token (see server/auth.ts's resolveBackendIdToken). Rather
// than surfacing a generic error the user has to manually recover from by
// finding the sign-out button, send them straight back through Google
// sign-in and return them to where they were.
function redirectToSignIn(): void {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/auth/google?returnTo=${encodeURIComponent(returnTo)}`);
}

/**
 * Talks to the local dev API server (see server/), which mirrors the real
 * backend's contract and constraints — see docs/backend-findings.md and
 * docs/decisions/ADR-0004-local-data-clone.md. The local read endpoint extends
 * the production contract with search and operational filters so pagination
 * never hides matching records.
 */
export class LiveInventoryApi implements InventoryApi {
  private async fetchJSON(path: string, init?: RequestInit): Promise<Record<string, unknown>> {
    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        ...init,
        headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
      });
    } catch (error) {
      throw new ApiError(
        error instanceof Error ? error.message : 'Network request failed',
        'NETWORK',
      );
    }
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (!response.ok) {
      if (response.status === 401) {
        redirectToSignIn();
        throw new ApiError('Your session expired. Redirecting to sign-in…', 'UNAUTHENTICATED');
      }
      const message =
        typeof body.error === 'string'
          ? body.error
          : typeof body.message === 'string'
            ? body.message
            : `Request failed with status ${response.status}`;
      const currentVersion =
        typeof body.currentVersion === 'number' ? body.currentVersion : undefined;
      // The relay's own errors (auth, etc.) use string codes like 'NOT_FOUND'.
      // The real backend reports errors as grpc-gateway JSON with the
      // canonical numeric gRPC status code instead (see GRPC_CODES) — map
      // that to the same string code so both contracts land on one switch.
      const code = GRPC_CODES[body.code as number] ?? body.code;
      switch (code) {
        case 'NOT_FOUND':
          throw new NotFoundError(message);
        case 'CONFLICT':
          throw new ConflictError(message, currentVersion);
        case 'DUPLICATE':
          throw new DuplicateError(message);
        case 'VALIDATION':
          throw new ValidationError(message);
        case 'FORBIDDEN':
          throw new ForbiddenError(message);
        default:
          // Some admin-only endpoints (e.g. subscriptions) reject
          // unauthorized callers with a plain Go error rather than a
          // status.Errorf(codes.PermissionDenied, ...), so it arrives here
          // as an uncoded Unknown/Internal error rather than a real 403 —
          // see docs/backend-findings.md. Recognize it by message the same
          // way version-mismatch messages are normalized to ConflictError.
          if (/not authorized/i.test(message)) throw new ForbiddenError(message);
          throw new ApiError(message, 'INTERNAL');
      }
    }
    return body;
  }

  async listItems(filters: ItemFilters = {}): Promise<Page<Item>> {
    const params = new URLSearchParams();
    if (filters.query) params.set('query', filters.query);
    if (filters.customerId) params.set('customerId', filters.customerId);
    if (filters.siteId) params.set('siteId', filters.siteId);
    if (filters.assignedSiteId) params.set('assignedSiteId', filters.assignedSiteId);
    if (filters.locationSiteId) params.set('locationSiteId', filters.locationSiteId);
    if (filters.partIds && filters.partIds.length > 0) {
      for (const partId of filters.partIds) params.append('partId', partId);
    } else if (filters.partId) {
      params.set('partId', filters.partId);
    }
    if (filters.statuses && filters.statuses.length > 0) {
      for (const status of filters.statuses) params.append('status', status);
    } else if (filters.status) {
      params.set('status', filters.status);
    }
    if (filters.needsAttention !== undefined) {
      params.set('needsAttention', String(filters.needsAttention));
    }
    if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
    if (filters.pageToken) params.set('pageToken', filters.pageToken);

    const response = await this.fetchJSON(`/v1/items?${params.toString()}`);
    const rawItems = Array.isArray(response.items) ? response.items : [];
    const data = rawItems.map((raw) => itemSchema.parse(raw));

    return {
      data,
      total: typeof response.total === 'number' ? response.total : data.length,
      ...(typeof response.nextPageToken === 'string'
        ? { nextPageToken: response.nextPageToken }
        : {}),
    };
  }

  async getItem(id: string): Promise<Item> {
    const raw = await this.fetchJSON(`/v1/items/${encodeURIComponent(id)}`);
    return itemSchema.parse(raw);
  }

  async createItem(input: CreateItemInput): Promise<Item> {
    const raw = await this.fetchJSON('/v1/items', {
      method: 'POST',
      body: JSON.stringify(withoutUndefined(input)),
    });
    return itemSchema.parse(raw);
  }

  async batchCreateItems(inputs: CreateItemInput[]): Promise<Item[]> {
    const raw = await this.fetchJSON('/v1/items:batchCreate', {
      method: 'POST',
      body: JSON.stringify({ items: inputs.map((input) => withoutUndefined(input)) }),
    });
    const rawItems = Array.isArray(raw.items) ? raw.items : [];
    return rawItems.map((item) => itemSchema.parse(item));
  }

  async updateItem(patch: ItemPatch): Promise<Item> {
    const { id, ...rest } = patch;
    const raw = await this.fetchJSON(`/v1/items/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(withoutUndefined(rest)),
    });
    return itemSchema.parse(raw);
  }

  async batchUpdateItems(patches: ItemPatch[]): Promise<Item[]> {
    const raw = await this.fetchJSON('/v1/items:batchUpdate', {
      method: 'POST',
      body: JSON.stringify({ items: patches.map((patch) => withoutUndefined(patch)) }),
    });
    const rawItems = Array.isArray(raw.items) ? raw.items : [];
    return rawItems.map((item) => itemSchema.parse(item));
  }

  async deleteItem(id: string): Promise<void> {
    await this.fetchJSON(`/v1/items/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async getItemUsages(ids: string[]): Promise<Record<string, ItemUsage>> {
    if (ids.length === 0) return {};
    // ListItemUsagesRequest only has a single `id` field, bound via path --
    // there's no id-set filter on the wire. id="-" (AIP-159 wildcard) returns
    // every cached row in one call; callers key into the result themselves.
    // (A per-item `ids=` query string was tried here previously and produced
    // a >75KB URL for the full fleet, which the backend rejected with 413.)
    const raw = await this.fetchJSON('/v1/items/-/usages');
    const rawUsages = Array.isArray(raw.itemUsages) ? raw.itemUsages : [];
    const usages = rawUsages.map((usage) => itemUsageSchema.parse(usage));
    return Object.fromEntries(usages.map((usage) => [usage.id, usage]));
  }

  async listItemRegister(country: string): Promise<ItemRegister> {
    const params = new URLSearchParams({ country });
    const raw = await this.fetchJSON(`/v1/items:register?${params.toString()}`);
    return itemRegisterSchema.parse(raw);
  }

  async listParts(): Promise<Part[]> {
    const raw = await this.fetchJSON('/v1/parts');
    const rawParts = Array.isArray(raw.parts) ? raw.parts : [];
    return rawParts.map((part) => partSchema.parse(part));
  }

  async listCustomers(): Promise<Customer[]> {
    const raw = await this.fetchJSON('/v1/customers');
    const rawCustomers = Array.isArray(raw.customers) ? raw.customers : [];
    return rawCustomers.map((customer) => customerSchema.parse(customer));
  }

  async listSites(customerId?: string): Promise<Site[]> {
    // Without an explicit pageSize the backend silently truncates to its own
    // default (well under the real ~250 site total) — same ceiling used for items.
    const params = new URLSearchParams({ pageSize: '5000' });
    if (customerId) params.set('customerId', customerId);
    const raw = await this.fetchJSON(`/v1/sites?${params.toString()}`);
    const rawSites = Array.isArray(raw.sites) ? (raw.sites as RawSite[]) : [];
    return rawSites.map((site) => siteSchema.parse(mapSiteCoordinates(site)));
  }

  async updateSiteContact(patch: SiteContactPatch): Promise<Site> {
    const { id, customerId, clear, ...rest } = patch;
    const params = new URLSearchParams();
    // UpdateSiteRequest.clear_mask, bound from the query string since the
    // body is the Site itself.
    if (clear && clear.length > 0) params.set('clearMask', clear.join(','));
    const query = params.toString();
    const raw = await this.fetchJSON(
      `/v1/customers/${encodeURIComponent(customerId)}/sites/${encodeURIComponent(id)}${query ? `?${query}` : ''}`,
      { method: 'PATCH', body: JSON.stringify(withoutUndefined(rest)) },
    );
    return siteSchema.parse(mapSiteCoordinates(raw));
  }

  async addSiteNote(customerId: string, siteId: string, note: SiteNoteInput): Promise<SiteNote> {
    const raw = await this.fetchJSON(
      `/v1/customers/${encodeURIComponent(customerId)}/sites/${encodeURIComponent(siteId)}/notes`,
      { method: 'POST', body: JSON.stringify({ kind: note.kind, text: note.text }) },
    );
    return siteNoteSchema.parse(raw);
  }

  async listSiteNotes(
    customerId: string,
    siteId: string,
    pageToken?: string,
  ): Promise<SiteNotePage> {
    const params = new URLSearchParams();
    if (pageToken) params.set('pageToken', pageToken);
    const query = params.toString();
    const raw = await this.fetchJSON(
      `/v1/customers/${encodeURIComponent(customerId)}/sites/${encodeURIComponent(siteId)}/notes${query ? `?${query}` : ''}`,
    );
    const notes = Array.isArray(raw.notes) ? raw.notes : [];
    return {
      notes: notes.map((note) => siteNoteSchema.parse(note)),
      nextPageToken: typeof raw.nextPageToken === 'string' ? raw.nextPageToken : undefined,
    };
  }

  async listLatestSiteNotes(customerId: string): Promise<SiteNote[]> {
    // AIP-159: "-" as the site lists every site's latest note and contact.
    return (await this.listSiteNotes(customerId, '-')).notes;
  }

  async listSitePenCounts(customerId: string): Promise<SitePenCounts[]> {
    const raw = await this.fetchJSON(
      `/v1/customers/${encodeURIComponent(customerId)}/sites/-/penCounts`,
    );
    const counts = Array.isArray(raw.penCounts) ? raw.penCounts : [];
    return counts.map((count) => sitePenCountsSchema.parse(count));
  }

  // ListUnits is bound to customers/{customer}/sites/{site}; "-" for the
  // site lists all of that customer's sites. Units are never listed across
  // customers, and there is no /v1/units route.
  async listUnits(customerId: string, siteId?: string): Promise<Unit[]> {
    const path = `/v1/customers/${encodeURIComponent(customerId)}/sites/${siteId ? encodeURIComponent(siteId) : '-'}/units`;
    const units: Unit[] = [];
    let pageToken = '';
    do {
      const params = new URLSearchParams({ pageSize: '5000' });
      if (pageToken) params.set('pageToken', pageToken);
      const raw = await this.fetchJSON(`${path}?${params.toString()}`);
      for (const row of Array.isArray(raw.units) ? (raw.units as unknown[]) : []) {
        const parsed = unitSchema.safeParse(row);
        // One malformed unit must not hide the rest.
        if (parsed.success) units.push(parsed.data);
        else console.warn('Skipped a unit that failed validation', parsed.error.issues[0]);
      }
      pageToken = typeof raw.nextPageToken === 'string' ? raw.nextPageToken : '';
    } while (pageToken);
    return units;
  }

  async getCustomerSubscription(customerId: string): Promise<Subscription> {
    const raw = await this.fetchJSON(
      `/v1/customers/${encodeURIComponent(customerId)}/subscription`,
    );
    return subscriptionSchema.parse(raw);
  }

  async createCustomerSubscription(
    customerId: string,
    input: CreateSubscriptionInput,
  ): Promise<Subscription> {
    // google.api.http `body: "subscription"` unwraps the HTTP body directly
    // into that field — the JSON sent here is the Subscription's own shape,
    // not `{ subscription: {...} }`.
    const raw = await this.fetchJSON(
      `/v1/customers/${encodeURIComponent(customerId)}/subscriptions`,
      { method: 'POST', body: JSON.stringify(withoutUndefined(input)) },
    );
    return subscriptionSchema.parse(raw);
  }

  async updateCustomerSubscription(
    customerId: string,
    patch: SubscriptionPatch,
  ): Promise<Subscription> {
    const { id, ...rest } = patch;
    const raw = await this.fetchJSON(
      `/v1/customers/${encodeURIComponent(customerId)}/subscriptions/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(withoutUndefined(rest)) },
    );
    return subscriptionSchema.parse(raw);
  }

  async updateSubscriptionFeatures(
    customerId: string,
    features: SubscriptionFeature[],
  ): Promise<Subscription> {
    // UpdateSubscriptionType rejects a request that sets name or version —
    // features is the only field this endpoint accepts — and it has no
    // response shape matching Subscription, so re-fetch the merged view
    // afterward rather than trying to reconcile two different API shapes.
    await this.fetchJSON(`/v1/customers/${encodeURIComponent(customerId)}/subscriptionType`, {
      method: 'PATCH',
      body: JSON.stringify({ features: features.map((name) => ({ name })) }),
    });
    return this.getCustomerSubscription(customerId);
  }

  async listAuditEvents(filters: AuditFilters = {}): Promise<Page<AuditEvent>> {
    const params = new URLSearchParams();
    if (filters.query) params.set('query', filters.query);
    if (filters.operation) params.set('operation', filters.operation);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
    if (filters.pageToken) params.set('pageToken', filters.pageToken);
    const raw = await this.fetchJSON(`/v1/audit?${params.toString()}`);
    const rawEvents = Array.isArray(raw.events) ? raw.events : [];
    return {
      data: rawEvents.map((event) => auditEventSchema.parse(event)),
      total: typeof raw.total === 'number' ? raw.total : rawEvents.length,
      ...(typeof raw.nextPageToken === 'string' ? { nextPageToken: raw.nextPageToken } : {}),
    };
  }

  async getItemHistory(
    itemId: string,
    options: { pageToken?: string; pageSize?: number } = {},
  ): Promise<Page<AuditEvent>> {
    const params = new URLSearchParams();
    if (options.pageToken) params.set('pageToken', options.pageToken);
    if (options.pageSize) params.set('pageSize', String(options.pageSize));
    const query = params.toString();
    const raw = await this.fetchJSON(
      `/v1/items/${encodeURIComponent(itemId)}/history${query ? `?${query}` : ''}`,
    );
    const rawEvents = Array.isArray(raw.history) ? raw.history : [];
    return {
      data: rawEvents.map((event) => auditEventSchema.parse(event)),
      ...(typeof raw.nextPageToken === 'string' ? { nextPageToken: raw.nextPageToken } : {}),
    };
  }
}

/**
 * Real site records nest coordinates under `locationInfo` rather than the flat
 * latitude/longitude fields this domain model uses, and store them as strings
 * that are sometimes literally "NaN". See SPEC.md 5.8: map views must degrade
 * gracefully when coordinates are absent or invalid.
 */
function mapSiteCoordinates(site: RawSite): Record<string, unknown> {
  const { locationInfo, latitude: rawLatitude, longitude: rawLongitude, ...rest } = site;
  const latitude = Number(locationInfo?.latitude ?? rawLatitude);
  const longitude = Number(locationInfo?.longitude ?? rawLongitude);
  return {
    ...rest,
    ...(Number.isFinite(latitude) ? { latitude } : {}),
    ...(Number.isFinite(longitude) ? { longitude } : {}),
  };
}

export const inventoryApi: InventoryApi = new LiveInventoryApi();
